import { ApolloQueryResult } from '@apollo/client';
import { message } from 'antd';
import { updateNoteToCard } from 'containers/MainLayout/actions';
import MainActionTypes from 'containers/MainLayout/constants';
import { selectBuyer, selectStore } from 'containers/MainLayout/selectors';
import { all, call, fork, join, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import translations from 'translations';
import {
  Product,
  ProductFilter,
  ProductSortField,
  QueryProductSearchArgs,
  ShowcaseItem,
  ShowcaseWidget,
  SortDirection,
  WidgetUnion,
} from 'types/schema';
import {
  addFavorite,
  getBrands,
  getBrandsRelatedToCategory,
  getHomeProducts,
  getHomeProductsByIds,
  getWidgetConfigV2,
  handlePutItemsToFavorite as handlePutItemsToFavoriteApi,
  removeFavorite,
  setProductNote,
} from 'utils/apollo';
import { isFbkBrand } from 'utils/fbkBrandIds';
import {
  calcFullPages,
  calcTotalPages,
  getCachedFirstShowcaseHint,
  getCachedTotalResults,
  getDayPlan,
  getDayPlanKey,
  getFirstShowcaseHintKey,
  getNextPage,
  getTotalResultsStorageKey,
  IFirstShowcaseHint,
  INITIAL_PAGE_SIZE,
  MAX_LOAD_MORE_PROBES,
  MAX_PRODUCTS,
  resolveBasePage,
  setCachedFirstShowcaseHint,
  setCachedTotalResults,
  setDayPlan,
} from 'utils/carouselRules';
import {
  IHomePreload,
  isHomePreloadConsumed,
  isHomePreloadUsable,
  mergePreservingOrder,
  readHomePreload,
  readRawHomePreload,
} from 'utils/homePreload';
import utilMessages from 'utils/messages';
import { isRealProduct } from 'utils/utilities';
import { getDateSeed } from 'utils/dateSeed';
import { createStoreIdChangeTracker } from 'utils/storeIdChangeTracker';
import { homeFavorite, homeProducts, homeSetProductNote, setWidgets } from './actions';
import ActionTypes from './constants';
import { selectLoading, selectSections, selectWidgets } from './selectors';
import { ILoadMoreSectionPayload, ISectionData } from './types';

const homePageStoreTracker = createStoreIdChangeTracker();

let loadedWidgetsRegion: string | undefined;

// Dựng params y hệt buildSectionFilter/buildSectionParams (dùng cho fetch thật) —
// từ `hint`, tức cấu hình widget đầu tiên đã ghi lại ở lần tải trang gần nhất
// (xem loadShowcaseItems, carouselRules.ts) thay vì từ một ShowcaseItem thật.
function buildFirstShowcaseParams(hint: IFirstShowcaseHint, region: string, page = 0): QueryProductSearchArgs {
  const isTagWidget = !!hint.filter?.tags?.length;
  const filter: ProductFilter = { ...hint.filter, regionCode: region };
  if (filter.categoryName && hint.id) {
    delete filter.categoryName;
    filter.categoryIds = [Number(hint.id)];
  }
  if (isTagWidget) {
    filter.withInStock = true;
  }
  const params: QueryProductSearchArgs = {
    sort: hint.sort.length ? hint.sort : [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: filter,
    pagination: { page: page, size: INITIAL_PAGE_SIZE },
  };
  if (!isTagWidget) {
    params.isBoostKamereoProducts = true;
  }
  return params;
}

// Chữ ký nhận dạng MỘT câu query, KHÔNG gồm page — mọi page của cùng query chia
// sẻ chung một totalPages nên phải cho ra cùng một khoá cache. Trích riêng các
// field ý nghĩa nên miễn nhiễm với:
// - __typename (widget config từ GraphQL đính kèm; checkTypename chỉ strip khi
//   gửi request, tức SAU khi so sánh ở đây)
// - key null/undefined (categoryName:null, limitedTimeOfferPeriod:null...)
// - regionCode (getHomeProducts tự set lại giống nhau cho cả hai)
// - thứ tự phần tử.
function queryKey(p: QueryProductSearchArgs) {
  const f: any = p.filter || {};
  return JSON.stringify({
    sort: (p.sort || []).map((s: any) => `${s.field}:${s.order}`),
    tags: [...(f.tags || [])].sort(),
    supplierNames: [...(f.supplierNames || [])].sort(),
    categoryIds: [...(f.categoryIds || [])].sort(),
    categoryName: f.categoryName || null,
    withInStock: !!f.withInStock,
    size: p.pagination?.size,
  });
}

// Khớp -> tái dùng data prefetch; khác -> fetch lại. Phải so cả page vì prefetch
// và getSectionData có thể cùng query nhưng bốc page khác nhau.
function sameQuery(a: QueryProductSearchArgs, b: QueryProductSearchArgs) {
  return queryKey(a) === queryKey(b) && a.pagination?.page === b.pagination?.page;
}

// Prefetch bốc page bằng ĐÚNG công thức getSectionData sẽ dùng, nếu không hai bên
// xin khác page -> sameQuery không khớp -> response prefetch vứt đi và phải gọi lại.
//
// KHÔNG BIẾT widget đầu tiên là widget nào lúc này — hàm này chạy TRƯỚC khi widgets
// về (đó là lý do nó tồn tại: chạy song song, không đợi). Đoán bằng `hint` đã ghi ở
// lần tải trang gần nhất (carouselRules.ts). Chưa từng vào trang này (chưa có hint)
// thì trả `null`, bỏ qua prefetch — getSectionData vẫn tự fetch bình thường sau khi
// widgets về, không mất gì so với việc không prefetch. Đoán sai (BE đổi widget đầu)
// thì sameQuery tự phát hiện lệch, rơi về fetch thật, loadShowcaseItems ghi hint mới.
function* prefetchFirstShowcase(region: string) {
  const hint = getCachedFirstShowcaseHint(getFirstShowcaseHintKey(region || 'HCM'));
  if (!hint) {
    return null;
  }

  const probe = buildFirstShowcaseParams(hint, region, 0);
  const seed = getDateSeed();
  const lang = window.localStorage.getItem('lang') || 'vi';
  const dayPlan = getDayPlan(getDayPlanKey(region || 'HCM', lang, hint.key), seed);
  const cachedFullPages =
    dayPlan?.fullPages ??
    calcFullPages(getCachedTotalResults(getTotalResultsStorageKey(region || 'HCM', queryKey(probe))));
  const page = resolveBasePage(dayPlan, seed, cachedFullPages);

  const params = buildFirstShowcaseParams(hint, region, page);
  const response = yield call(getHomeProducts, params, true);
  return { params: params, response: response };
}

function buildSectionFilter(section: ShowcaseItem): { filterConfig: ProductFilter; isTagWidget: boolean } {
  const { filter } = section;
  const filterConfig: ProductFilter = { ...filter };
  if (filter) {
    if (filterConfig.categoryName && section.id) {
      delete filterConfig.categoryName;
      filterConfig['categoryIds'] = [Number(section.id)];
    }
  }
  const isTagWidget = !!filter?.tags?.length;

  if (isTagWidget) {
    filterConfig.withInStock = true;
  }
  return { filterConfig: filterConfig, isTagWidget: isTagWidget };
}

function buildSectionParams(
  section: ShowcaseItem,
  filterConfig: ProductFilter,
  isTagWidget: boolean,
  page: number,
): QueryProductSearchArgs {
  const { sort } = section;
  const params: QueryProductSearchArgs = {
    sort: sort.length ? sort : [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: filterConfig,
    pagination: { page: page, size: INITIAL_PAGE_SIZE },
  };
  if (!isTagWidget) {
    params.isBoostKamereoProducts = true;
  }
  return params;
}

function* fetchSectionPage(params: QueryProductSearchArgs, noBatch = false, prefetchTask: any = null) {
  let response: any;
  // Không cần chặn theo page: sameQuery đã so cả page, sai page thì tự khắc không tái dùng.
  if (prefetchTask) {
    const prefetched = yield join(prefetchTask);
    if (prefetched && !prefetched.response?.errors && sameQuery(prefetched.params, params)) {
      response = prefetched.response;
    }
    // Lệch ở đây nghĩa là prefetch bắn phí một request. Thủ phạm hay gặp nhất là
    // hint (widget đầu tiên lần tải trang trước) đã lỗi thời so với widget config
    // hiện tại — loadShowcaseItems sẽ ghi hint mới ngay sau request này.
  }
  if (!response) {
    response = yield call(getHomeProducts, params, noBatch);
  }
  return response;
}

// Dựng section thẳng từ khối server đã nhét vào HTML rồi lấy nốt giá + yêu thích ở
// nền. Người dùng thấy khung sản phẩm ngay khi bundle chạy, không đợi vòng mạng nào.
// Trả false khi snapshot đã cũ (có item hết hàng sau lúc server cache) để caller fetch live.
function* hydrateSectionFromPreload(
  section: ShowcaseItem,
  preload: IHomePreload,
  dayPlanKey: string,
  totalResultsStorageKey: string,
  isTagWidget: boolean,
) {
  const best = preload.topShowcase as NonNullable<IHomePreload['topShowcase']>;
  const nextPage = getNextPage(best.basePage, best.totalPages);

  // `totalResults`/`fullPages` do server gửi kèm; bản preload cũ chưa có thì suy ngược từ
  // totalPages (chấp nhận sai số ở trang cuối, tự đúng lại sau lần fetch thật đầu tiên).
  const preTotalResults = best.totalResults ?? best.totalPages * INITIAL_PAGE_SIZE;
  const preFullPages = best.fullPages ?? calcFullPages(preTotalResults);

  setCachedTotalResults(totalResultsStorageKey, preTotalResults);
  setDayPlan(dayPlanKey, {
    seed: preload.seed,
    basePage: best.basePage,
    totalPages: best.totalPages,
    fullPages: preFullPages,
  });

  yield put(
    homeProducts.success({
      [section.key]: {
        data: best.data,
        error: false,
        basePage: best.basePage,
        nextPage: nextPage,
        pagesFetched: 1,
        hasMore: best.data.length < MAX_PRODUCTS && best.totalPages > 1,
        totalPages: best.totalPages,
        loadingMore: false,
        pendingHydration: true,
      } as ISectionData,
    }),
  );

  const priced = yield call(
    getHomeProductsByIds,
    best.data.map((p) => p.id),
  );

  if (!priced.length) {
    const sections = yield select(selectSections());
    yield put(
      homeProducts.success({
        [section.key]: { ...sections[section.key], pendingHydration: false },
      }),
    );
    return true;
  }

  // Tag widget luôn query withInStock, nên item hết hàng ở đây chỉ có thể do snapshot cũ.
  // Lọc bỏ thì thiếu card (chỉ hỏi lại đúng id của snapshot), nên bỏ cả snapshot để
  // fetch live page đó — BE tự đôn item còn hàng lên cho đủ.
  if (isTagWidget && (priced as Product[]).some((p) => p.inStock === false)) {
    return false;
  }

  const latest = yield select(selectSections());

  if (latest[section.key] && latest[section.key].basePage !== best.basePage) {
    yield put(
      homeProducts.success({
        [section.key]: { ...latest[section.key], pendingHydration: false },
      }),
    );
    return true;
  }

  const current = (latest[section.key]?.data || best.data) as Product[];
  yield put(
    homeProducts.success({
      [section.key]: {
        ...latest[section.key],
        data: mergePreservingOrder(current, priced as Product[]),
        pendingHydration: false,
      },
    }),
  );
  return true;
}

function* getSectionData(section: ShowcaseItem, noBatch = false, prefetchTask: any = null) {
  const { filterConfig, isTagWidget } = buildSectionFilter(section);
  const buyer = yield select(selectBuyer());
  const regionLocal = window.localStorage.getItem('region') || 'HCM';
  const region = buyer?.id ? buyer.region : regionLocal;
  filterConfig.regionCode = region;

  const lang = window.localStorage.getItem('lang') || 'vi';
  const seed = getDateSeed();
  const probeParams = buildSectionParams(section, filterConfig, isTagWidget, 0);
  const storageKey = getTotalResultsStorageKey(region || 'HCM', queryKey(probeParams));
  const dayPlanKey = getDayPlanKey(region || 'HCM', lang, section.key);

  const preload = readHomePreload(region || 'HCM', lang, section.key, seed);
  if (preload) {
    const hydrated = yield call(hydrateSectionFromPreload, section, preload, dayPlanKey, storageKey, isTagWidget);
    if (hydrated) {
      return;
    }
    // Snapshot cũ -> rơi xuống fetch live cùng basePage (day plan vừa ghi) với withInStock.
  }

  const dayPlan = getDayPlan(dayPlanKey, seed);
  const cachedFullPages = dayPlan?.fullPages ?? calcFullPages(getCachedTotalResults(storageKey));

  const sectionsBeforeFetch = yield select(selectSections());
  const dataBeforeFetch = (sectionsBeforeFetch[section.key]?.data || []) as unknown[];
  const isRefetch = dataBeforeFetch.some(isRealProduct);

  yield put(
    homeProducts.request({
      [section.key]: isRefetch
        ? { ...sectionsBeforeFetch[section.key], error: false }
        : { data: Array.from({ length: 6 }).fill(0), error: false },
    }),
  );

  let productData: Product[] = [];
  let basePage = resolveBasePage(dayPlan, seed, cachedFullPages);
  let totalPages = 1;

  try {
    let response = yield call(
      fetchSectionPage,
      buildSectionParams(section, filterConfig, isTagWidget, basePage),
      noBatch,
      prefetchTask,
    );

    // basePage trượt ra ngoài biên (catalog co lại từ lần trước) -> lùi về page 0.
    if (!response?.errors && !response?.data?.length && basePage !== 0) {
      basePage = 0;
      response = yield call(
        fetchSectionPage,
        buildSectionParams(section, filterConfig, isTagWidget, 0),
        noBatch,
        prefetchTask,
      );
    }

    if (response?.errors) {
      yield put(homeProducts.failure({ [section.key]: { data: [], error: true } }));
      return;
    }

    const totalResults = response.totalResults || 0;
    totalPages = calcTotalPages(totalResults);
    setCachedTotalResults(storageKey, totalResults);
    setDayPlan(dayPlanKey, {
      seed: seed,
      basePage: basePage,
      totalPages: totalPages,
      fullPages: calcFullPages(totalResults),
    });
    productData = response.data || [];
  } catch (e) {
    yield put(homeProducts.failure({ [section.key]: { data: [], error: true } }));
    return;
  }

  // Load-more chạy vòng qua các page (basePage+1, ..., totalPages-1, 0, 1, ...) rồi dừng khi
  // đã duyệt hết totalPages. Nếu không wrap, section bốc trúng page cuối sẽ kẹt ở 1 page.
  const nextPage = getNextPage(basePage, totalPages);
  const pagesFetched = 1;
  const hasMore = productData.length < MAX_PRODUCTS && pagesFetched < totalPages;

  yield put(
    homeProducts.success({
      [section.key]: {
        data: productData,
        error: false,
        basePage: basePage,
        nextPage: nextPage,
        pagesFetched: pagesFetched,
        hasMore: hasMore,
        totalPages: totalPages,
        loadingMore: false,
      } as ISectionData,
    }),
  );
}

function* handleLoadMoreSection(action: { type: string; payload: ILoadMoreSectionPayload }) {
  const { payload } = action;

  const { sectionKey, sort, filter, isTagWidget: payloadIsTag } = payload;

  const sections = yield select(selectSections());

  const currentSection: ISectionData = sections[sectionKey];

  if (!currentSection || currentSection.loadingMore || !currentSection.hasMore) {
    return;
  }

  const totalPages = currentSection.totalPages ?? 1;

  const pagesFetched = currentSection.pagesFetched ?? 1;

  if (pagesFetched >= totalPages) {
    yield put(
      homeProducts.success({
        [sectionKey]: {
          ...currentSection,
          hasMore: false,
          loadingMore: false,
        },
      }),
    );
    return;
  }
  const pageToFetch = (currentSection.nextPage ?? getNextPage(currentSection.basePage ?? 0, totalPages)) % totalPages;

  yield put(
    homeProducts.success({
      [sectionKey]: {
        ...currentSection,
        loadingMore: true,
      },
    }),
  );

  const buyer = yield select(selectBuyer());

  const regionLocal = window.localStorage.getItem('region') || 'HCM';

  const region = buyer?.id ? buyer.region : regionLocal;

  const filterConfig: ProductFilter = { ...filter };

  if (filterConfig.categoryName && payload.sectionId) {
    delete filterConfig.categoryName;

    filterConfig.categoryIds = [Number(payload.sectionId)];
  }

  const isTagWidget = payloadIsTag ?? !!filter?.tags?.length;

  if (isTagWidget) {
    filterConfig.withInStock = true;
  }

  filterConfig.regionCode = region;

  const buildParams = (page: number): QueryProductSearchArgs => {
    const p: QueryProductSearchArgs = {
      sort: sort?.length ? sort : [{ field: ProductSortField.Name, order: SortDirection.Asc }],
      filter: filterConfig,
      pagination: { page: page, size: INITIAL_PAGE_SIZE },
    };

    if (!isTagWidget) {
      p.isBoostKamereoProducts = true;
    }

    return p;
  };

  const lang = window.localStorage.getItem('lang') || 'vi';
  const dayPlanKey = getDayPlanKey(region || 'HCM', lang, sectionKey);
  const seenIds = new Set((currentSection.data as Product[]).map((p) => p?.id));

  let probePage = pageToFetch;
  let pagesProbed = 0;
  let freshTotalPages = totalPages;
  let freshTotalResults = 0;
  let lastPage: Product[] = [];
  let fresh: Product[] = [];
  let failed = false;

  while (pagesProbed < MAX_LOAD_MORE_PROBES) {
    const params = buildParams(probePage);
    const response = yield call(getHomeProducts, params);

    pagesProbed += 1;

    if (response?.errors) {
      failed = true;
      break;
    }

    freshTotalResults = response.totalResults || 0;
    freshTotalPages = calcTotalPages(freshTotalResults);
    setCachedTotalResults(getTotalResultsStorageKey(region || 'HCM', queryKey(params)), freshTotalResults);

    lastPage = (response.data || []) as Product[];
    fresh = lastPage.filter((p) => p?.id && !seenIds.has(p.id));

    if (fresh.length || !lastPage.length || pagesFetched + pagesProbed >= freshTotalPages) {
      break;
    }

    probePage = getNextPage(probePage, freshTotalPages);
  }

  const latestSections = yield select(selectSections());

  const latestSection: ISectionData = latestSections[sectionKey] || currentSection;

  if (failed) {
    yield put(
      homeProducts.success({
        [sectionKey]: {
          ...latestSection,
          loadingMore: false,
          hasMore: false,
        },
      }),
    );
    return;
  }

  // Catalog đổi giữa ngày -> day plan phải biết totalPages mới, nhưng GIỮ NGUYÊN basePage
  // đã chốt: đổi basePage giữa phiên là đổi luôn list user đang cuộn.
  const dayPlan = getDayPlan(dayPlanKey, getDateSeed());

  if (dayPlan && dayPlan.totalPages !== freshTotalPages) {
    setDayPlan(dayPlanKey, {
      ...dayPlan,
      totalPages: freshTotalPages,
      fullPages: freshTotalResults > 0 ? calcFullPages(freshTotalResults) : dayPlan.fullPages,
    });
  }

  // Lọc lại theo data MỚI NHẤT: vòng probe ở trên có thể mất vài lượt mạng, trong lúc đó section
  // đã kịp đổi (đổi store refresh giá, toggle yêu thích...).
  const latestIds = new Set((latestSection.data as Product[]).map((p) => p?.id));

  const appended = fresh.filter((p) => !latestIds.has(p.id));

  const merged = [...(latestSection.data as Product[]), ...appended].slice(0, MAX_PRODUCTS);

  const nextPage = getNextPage(probePage, freshTotalPages);

  const nextPagesFetched = pagesFetched + pagesProbed;

  const hasMore = merged.length < MAX_PRODUCTS && nextPagesFetched < freshTotalPages && lastPage.length > 0;

  yield put(
    homeProducts.success({
      [sectionKey]: {
        ...latestSection,
        data: merged,
        error: false,
        nextPage: nextPage,
        pagesFetched: nextPagesFetched,
        hasMore: hasMore,
        totalPages: freshTotalPages,
        loadingMore: false,
      },
    }),
  );
}

const EXTRA_TRUSTED_BRANDS_FETCH = 60;

function* getBrandsData() {
  // FBK first: OZAX, MORI, ACECOOK, VINABEEF, THIENLONG, SG LAGER
  const topBrandsQA = [1096, 1093, 1100, 1101, 1102, 447, 52, 51, 50, 49, 48, 47, 46, 45, 44, 43, 42, 41, 40, 39];
  const topBrandsPROD = [
    1176, 1154, 1242, 1243, 1213, 225, 43, 158, 1, 101, 106, 31, 220, 10, 194, 144, 19, 36, 134, 40, 127, 72, 246, 3,
    45, 4,
  ];

  let brandIds = ['qa', 'development'].includes(process.env.APP_ENV as string) ? topBrandsQA : topBrandsPROD;
  const buyer = yield select(selectBuyer);
  const regionLocal = window.localStorage.getItem('region') || 'HCM';
  if (buyer.region === 'HN' || regionLocal === 'HN') {
    brandIds = brandIds.slice(6);
  }
  // Priority (hardcoded) brands + extra brands in the same order as the brand page (/thuong-hieu).
  const [brandResponse, extraResponse] = yield all([
    call(getBrands, {
      brandIds: brandIds,
      pagination: { page: 0, size: brandIds.length },
    }),
    call(getBrandsRelatedToCategory, {
      filter: { categoryIds: [], enhanceMoreCategoriesRelated: true },
      pagination: { page: 0, size: EXTRA_TRUSTED_BRANDS_FETCH },
    }),
  ]);

  if (brandResponse.errors) {
    yield put(homeProducts.failure({ ['TRUSTED_BRANDS']: { data: [], error: true } }));
  } else {
    const arrangedBrands: any[] = [];
    brandIds.forEach((brandId) => {
      const brand = brandResponse?.data?.find((item) => Number(item.id) === brandId);
      if (brand) {
        arrangedBrands.push(brand);
      }
    });
    // Priority brands stay first (BestForRestaurants reads the first 6); extras are appended.
    // Extra fetch failing just leaves the priority list, as before.
    const extraBrands = extraResponse?.errors
      ? []
      : (extraResponse?.data || []).filter(
          (brand) => brand?.imageUrl && !isFbkBrand(brand.id) && !brandIds.includes(Number(brand.id)),
        );
    yield put(
      homeProducts.success({ ['TRUSTED_BRANDS']: { data: [...arrangedBrands, ...extraBrands], error: false } }),
    );
  }
}

function* loadShowcaseItems(
  currentShowcaseItems: ShowcaseWidget[],
  widgetItems: WidgetUnion[],
  prefetchTask: any = null,
) {
  const loading = yield select(selectLoading());
  const showcaseWidgets = widgetItems.filter((i) => i.type === 'ShowcaseWidget') as unknown as ShowcaseWidget[];
  const newShowcaseConfig = showcaseWidgets.filter(
    (sw) =>
      !currentShowcaseItems.find((csw) => csw.widgetId === sw.widgetId) ||
      !Object.keys(loading).length ||
      loading[sw.widgetId],
  );

  const firstShowcase = showcaseWidgets[0];

  if (firstShowcase?.item) {
    const buyer = yield select(selectBuyer());

    const regionLocal = window.localStorage.getItem('region') || 'HCM';

    const region = (buyer?.id ? buyer.region : regionLocal) || 'HCM';

    setCachedFirstShowcaseHint(getFirstShowcaseHintKey(region), {
      key: firstShowcase.item.key,
      sort: firstShowcase.item.sort,
      filter: firstShowcase.item.filter,
      id: firstShowcase.item.id,
    });
  }

  for (const item of newShowcaseConfig) {
    const section = item.item;

    const isFirstShowcase = item.widgetId === firstShowcase?.widgetId;

    yield fork(getSectionData, section, isFirstShowcase, isFirstShowcase ? prefetchTask : null);
  }
}

export function* getPurchasedItemsMightFavorite(mightFavoriteProductsWidget) {
  const store = yield select(selectStore());
  yield put(
    homeProducts.success({
      [mightFavoriteProductsWidget.widgetId]: {
        data: [],
        error: false,
        loading: true,
        isAdded: false,
      },
    }),
  );
  const params: QueryProductSearchArgs = {
    sort: [{ field: ProductSortField.Name, order: SortDirection.Asc }],
    filter: {
      isMightFavorite: true,
      storeUuid: store.id,
    },
    pagination: { page: 0, size: 100 },
  };
  const response = yield call(getHomeProducts, params);
  if (!response.errors) {
    yield put(
      homeProducts.success({
        [mightFavoriteProductsWidget.widgetId]: {
          data: response.data,
          error: false,
          loading: false,
          isAdded: !response.data.length,
        },
      }),
    );
  } else {
    yield put(
      homeProducts.failure({
        [mightFavoriteProductsWidget.widgetId]: {
          data: [],
          error: true,
          loading: false,
          isAdded: true,
        },
      }),
    );
  }
}

function* fetchWidgets(region: string, prefetchTask: any = null, forceReloadShowcase = false) {
  const response = yield call(getWidgetConfigV2, { regions: [region] });
  console.log(response);
  if (!(response as unknown as ApolloQueryResult<WidgetUnion[]>).errors) {
    loadedWidgetsRegion = region;
    const currentWidgets = yield select(selectWidgets());
    const showcaseWidgets: ShowcaseWidget[] = forceReloadShowcase
      ? []
      : (currentWidgets.filter((i: WidgetUnion) => i?.type === 'ShowcaseWidget') as ShowcaseWidget[]);
    yield fork(loadShowcaseItems, showcaseWidgets, [...response], prefetchTask);
    yield put(setWidgets(response));
  }
}

function* reloadHomePageWidgets(region: string) {
  // Không cần guard theo region nữa: prefetchFirstShowcase tự bỏ qua nếu region này
  // chưa có hint (xem prefetchFirstShowcase), hoạt động cho mọi region như nhau.
  const firstShowcaseTask = yield fork(prefetchFirstShowcase, region);
  yield call(fetchWidgets, region, firstShowcaseTask, true);
}

function* onRegionChange(actionData: { type: string; payload: string }) {
  const region = actionData.payload || localStorage.getItem('region') || 'HCM';
  yield call(reloadHomePageWidgets, region);
}

function* refreshOneSectionPrices(sectionKey: string) {
  const sections = yield select(selectSections());
  const ids = ((sections[sectionKey]?.data || []) as Product[]).filter(isRealProduct).map((p) => p.id);

  if (!ids.length) {
    return;
  }

  const priced = yield call(getHomeProductsByIds, ids);

  if (!priced.length) {
    return;
  }

  const latest = yield select(selectSections());

  yield put(
    homeProducts.success({
      [sectionKey]: {
        ...latest[sectionKey],
        data: mergePreservingOrder((latest[sectionKey]?.data || []) as Product[], priced as Product[]),
      },
    }),
  );
}

function* refreshSectionPrices() {
  const sections = yield select(selectSections());

  for (const sectionKey of Object.keys(sections)) {
    const section: ISectionData = sections[sectionKey];

    if (section?.basePage === undefined) {
      continue;
    }

    if (!(section.data as Product[])?.some(isRealProduct)) {
      continue;
    }

    yield fork(refreshOneSectionPrices, sectionKey);
  }
}

function* reloadHomePageOnStoreChange() {
  const store = yield select(selectStore());
  if (!homePageStoreTracker.hasChanged(store?.id)) {
    return;
  }
  const widgets = yield select(selectWidgets());
  if (!widgets.length) {
    return;
  }
  const buyer = yield select(selectBuyer());
  const region = buyer?.region || window.localStorage.getItem('region') || 'HCM';
  if (region !== loadedWidgetsRegion) {
    yield call(reloadHomePageWidgets, region);
    return;
  }
  yield call(refreshSectionPrices);
}

// Nếu buyer về sau với region khác region đã đoán (lần đầu đăng nhập trên máy
// mới) thì fetch lại widgets cho đúng region.
function* refetchWidgetsOnRegionMismatch(usedRegion: string) {
  const loggedIn = !!localStorage.getItem('token');
  const buyerNow = yield select(selectBuyer());
  if (!loggedIn || buyerNow?.id) {
    return;
  }
  yield take(MainActionTypes.FETCH_BUYER_SUCCESS);
  const buyer = yield select(selectBuyer());
  if (buyer?.region && buyer.region !== usedRegion) {
    yield call(fetchWidgets, buyer.region);
  }
}

function* loadWidgets() {
  const buyer = yield select(selectBuyer());
  const region = buyer?.region || localStorage.getItem('region') || 'HCM';
  const lang = localStorage.getItem('lang') || 'vi';
  const hint = getCachedFirstShowcaseHint(getFirstShowcaseHintKey(region));
  const preloadUsable =
    !isHomePreloadConsumed() && isHomePreloadUsable(readRawHomePreload(), region, lang, getDateSeed(), hint?.key);

  const firstShowcaseTask = preloadUsable ? null : yield fork(prefetchFirstShowcase, region);
  yield fork(refetchWidgetsOnRegionMismatch, region);
  yield call(fetchWidgets, region, firstShowcaseTask);
}

function* homeFavoriteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.HOME_FAVORITE);
    yield put(homeFavorite.request());

    const {
      product: { isFavorite, id },
      index,
      section,
    } = payload;

    const response = yield call(isFavorite ? removeFavorite : addFavorite, { id: id });
    if (response.errors) {
      yield put(homeFavorite.failure({ index: index, section: section }));
    } else {
      yield put(homeFavorite.success({ index: index, section: section, isFavorite: !isFavorite }));
    }
  }
}

function* homeSetProductNoteFlow() {
  while (true) {
    const { payload } = yield take(ActionTypes.HOME_SET_PRODUCT_NOTE);
    yield put(homeSetProductNote.request());

    const { id, note, index, section } = payload;
    const response = yield call(setProductNote, { input: { productId: id, note: note } });
    if (response.errors) {
      yield put(homeSetProductNote.failure(response.errors));
      message.error(translations(utilMessages[payload.note ? 'addItemNoteFailed' : 'deleteItemNoteFailed']));
    } else {
      yield put(homeSetProductNote.success({ note: note, index: index, section: section }));
      yield put(updateNoteToCard(response.product));
      message.success(translations(utilMessages[payload.note ? 'addItemNoteSuccess' : 'deleteItemNoteSuccess']));
    }
  }
}

function* handlePutItemsToFavorite() {
  while (true) {
    const { payload } = yield take(ActionTypes.HANDLE_ADD_ALL_TO_FAVORITE);
    const response = yield call(handlePutItemsToFavoriteApi, { itemUuids: payload });
    if (!response.errors) {
      const sections = yield select(selectSections());
      const isMightFavoriteSection = sections['MIGHT_FAVORITE_PRODUCTS'];
      yield put(
        homeProducts.success({
          ['MIGHT_FAVORITE_PRODUCTS']: {
            ...isMightFavoriteSection,
            isAdded: true,
          },
        }),
      );
    }
  }
}

export default function* saga() {
  // const googleBot = yield call(isGoogleBot);
  // if (!googleBot) {
  //   const homepagePopupTreatment = yield select(selectTreatmentValueByName(TreatmentNames.BUYER_WEB_HOME_PAGE_POPUP));
  //   if (homepagePopupTreatment) {
  //     yield call(loadHomepagePopUp);
  //   }
  // }

  // fork thay vì call: getBrandsData không cần chờ widgets về xong
  const initialStore = yield select(selectStore());
  homePageStoreTracker.sync(initialStore?.id);
  yield fork(loadWidgets);
  yield fork(getBrandsData);
  yield fork(homeFavoriteFlow);
  yield fork(homeSetProductNoteFlow);
  yield fork(handlePutItemsToFavorite);
  yield takeEvery(ActionTypes.LOAD_MORE_SECTION, handleLoadMoreSection);
  yield takeLatest(MainActionTypes.CHANGE_SELECTED_STORE, reloadHomePageOnStoreChange);
  yield takeLatest(MainActionTypes.REGION_CHANGED, onRegionChange);
}
